import { useEffect, useMemo, useState } from 'react'
import { IconButton, Tooltip } from '@mui/material'
import { DatePicker, Modal, Select } from 'antd'
import dayjs from 'dayjs'
import moment from 'moment'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { AiOutlineDelete, AiOutlineEdit } from 'react-icons/ai'
import { BsArrowRepeat } from 'react-icons/bs'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { ButtonFilledAutoWidth, ButtonOutlinedAutoWidth } from '../../components/button'
import SettingsMenu from '../staticscreens/SettingsMenu'
import { GetFileStandardService } from '../../services/admin/FileStandardServices'
import { GetClassStreamService } from '../../services/admin/StudentStreamServices'
import { DeleteSubjectService, GetSubjectService } from '../../services/admin/SubjectServices'

function SubjectList() {
    const initialFilters = {
        filter_standard: '',
        stream: '',
        substream: '',
        search: '',
        from_date: '',
        to_date: ''
    }

    const [data, setdata] = useState({})
    const [page, setpage] = useState(1)
    const [selecteddata, setselecteddata] = useState({})
    const [modal, setModal] = useState(false)
    const [standards, setstandards] = useState([])
    const [streams, setstreams] = useState([])
    const [substreams, setsubstreams] = useState([])
    const [filters, setfilters] = useState(initialFilters)
    const [appliedFilters, setappliedFilters] = useState(initialFilters)
    const navigate = useNavigate()

    useEffect(() => {
        getFilterOptions()
    }, [])

    useEffect(() => {
        getData()
    }, [page, appliedFilters.filter_standard, appliedFilters.stream, appliedFilters.substream, appliedFilters.search, appliedFilters.from_date, appliedFilters.to_date])

    async function getFilterOptions() {
        const [standardResponse, streamResponse] = await Promise.all([
            GetFileStandardService(),
            GetClassStreamService()
        ])

        const streamDatas = streamResponse?.data?.datas || []

        setstandards((standardResponse?.data?.datas || []).map((item) => ({
            value: item._id,
            label: item.standard || item.label,
            data: item
        })))

        setstreams(streamDatas.filter((item) => !item?.parent_stream).map((item) => ({
            value: item.name,
            label: item.name,
            data: item
        })))

        setsubstreams(streamDatas.filter((item) => item?.parent_stream).map((item) => ({
            value: item.name,
            label: item.name,
            data: item
        })))
    }

    async function getData() {
        const response = await GetSubjectService({ ...appliedFilters, page })
        setdata(response?.data)
    }

    async function deleteSubject() {
        const response = await DeleteSubjectService(selecteddata?._id)
        if (response?.status === 200) {
            setModal(false)
            toast.success(response?.data?.message || 'Deleted successfully')
            setselecteddata({})
            getData()
        }
    }

    function handleFilterChange(value, field) {
        if (field === 'stream') {
            setfilters((prev) => ({ ...prev, stream: value || '', substream: '' }))
            return
        }

        setfilters((prev) => ({ ...prev, [field]: value || '' }))
    }

    function applyFilters() {
        setpage(1)
        setappliedFilters(filters)
    }

    function resetFilters() {
        setpage(1)
        setfilters(initialFilters)
        setappliedFilters(initialFilters)
    }

    const visibleSubstreams = useMemo(() => {
        return substreams.filter((item) => {
            if (!filters.stream) {
                return true
            }

            const parentStream = item?.data?.parent_stream?.name || item?.data?.parent_stream
            return parentStream === filters.stream
        })
    }, [substreams, filters.stream])

    return (
        <div className='md:h-screen pt-5 md:pt-0 md:max-h-screen box-border md:overflow-hidden'>
            <Modal
                keepMounted
                open={modal}
                onCancel={() => setModal(false)}
                width={300}
                footer={false}
                closable={false}
            >
                <div>
                    <h6 className='font-bold text-[15px] text-center mb-2 w-full'>Are you sure?</h6>
                    <h6 className='bg-slate-100 text-center text-[12px] p-1.5 font-[400]'>After deleting you cannot retrieve it back before deleting check once whether you have used it in some data</h6>
                    <div className='flex justify-end mt-3 '>
                        <ButtonOutlinedAutoWidth btnName={'Cancel'} onClick={() => setModal(false)} />
                        <div className='ml-2'>
                            <ButtonFilledAutoWidth btnName={'Confirm'} onClick={() => deleteSubject()} />
                        </div>
                    </div>
                </div>
            </Modal>

            <div className='block sm:flex'>
                <SettingsMenu />

                <div className='md:w-[88%] px-4'>
                    <div className='pt-5'>
                        <div className='md:flex flex-wrap items-center border-b justify-between pb-2 gap-3'>
                            <div className='flex items-center justify-between gap-3'>
                                <h6 className='font-[800] text-[14px]'>Total Subject ({data?.pagination?.total})</h6>

                                <div className='hidden md:flex items-center text-[12px]'>
                                    <h6 className='mr-2 font-[500]'>
                                        {page === 1 ? (data?.datas?.length > 0 ? 1 : 0) : (((page - 1) * (data?.pagination?.limit || 0)) + 1)} - {Math.min(page * (data?.pagination?.limit || 0), data?.pagination?.total || 0)} of {data?.pagination?.total || 0}
                                    </h6>
                                    <IconButton onClick={resetFilters}><BsArrowRepeat size={16} /></IconButton>
                                    <div>
                                        <IconButton onClick={() => { if (page > 1) setpage(page - 1) }}>
                                            <FiChevronLeft className={`${page === 1 ? 'opacity-50' : 'opacity-100'}`} size={16} />
                                        </IconButton>
                                        <IconButton onClick={() => { if (page < (data?.pagination?.totalPages || 1)) setpage(page + 1) }}>
                                            <FiChevronRight className={`${data?.pagination?.totalPages === page || !data?.pagination?.totalPages ? 'opacity-50' : 'opacity-100'}`} size={16} />
                                        </IconButton>
                                    </div>
                                </div>
                            </div>

                            <div className='flex flex-wrap items-center gap-2'>
                                <input
                                    value={filters.search}
                                    type='text'
                                    placeholder='Search Subject'
                                    onChange={(event) => handleFilterChange(event.target.value, 'search')}
                                    className='w-full md:w-[150px] border text-sm py-[4px] focus:ring-0 focus:outline-0 text-[13px] px-2 rounded-md border-slate-300'
                                />

                                <DatePicker
                                    size='small'
                                    format='YYYY-MM-DD'
                                    allowClear
                                    placeholder='From Date'
                                    value={filters.from_date ? dayjs(filters.from_date) : null}
                                    onChange={(_, dateString) => handleFilterChange(dateString, 'from_date')}
                                    className='subject-list-date-picker h-[30px] w-full md:w-[140px] border-slate-300 mr-0'
                                />

                                <DatePicker
                                    size='small'
                                    format='YYYY-MM-DD'
                                    allowClear
                                    placeholder='To Date'
                                    value={filters.to_date ? dayjs(filters.to_date) : null}
                                    onChange={(_, dateString) => handleFilterChange(dateString, 'to_date')}
                                    className='subject-list-date-picker h-[30px] w-full md:w-[140px] border-slate-300 mr-0'
                                />

                                <div className='w-full md:w-[140px]'>
                                    <Select
                                        value={filters.filter_standard || undefined}
                                        allowClear
                                        bordered={false}
                                        placeholder='Filter Standard'
                                        onChange={(value) => handleFilterChange(value, 'filter_standard')}
                                        options={standards}
                                        className='w-full placeholder-text:12px border rounded-[6px] h-[28px] border-slate-300 outline-0 focus:outline-0 focus:ring-0 focus:border-transparent'
                                    />
                                </div>

                                <div className='w-full md:w-[140px]'>
                                    <Select
                                        value={filters.stream || undefined}
                                        allowClear
                                        bordered={false}
                                        placeholder='Stream'
                                        onChange={(value) => handleFilterChange(value, 'stream')}
                                        options={streams}
                                        className='w-full placeholder-text:12px border rounded-[6px] h-[28px] border-slate-300 outline-0 focus:outline-0 focus:ring-0 focus:border-transparent'
                                    />
                                </div>

                                <div className='w-full md:w-[140px]'>
                                    <Select
                                        value={filters.substream || undefined}
                                        allowClear
                                        bordered={false}
                                        placeholder='Sub Stream'
                                        onChange={(value) => handleFilterChange(value, 'substream')}
                                        options={visibleSubstreams}
                                        className='w-full placeholder-text:12px border rounded-[6px] h-[28px] border-slate-300 outline-0 focus:outline-0 focus:ring-0 focus:border-transparent'
                                    />
                                </div>

                                <ButtonOutlinedAutoWidth btnName='Apply Filter' onClick={applyFilters} />
                                <ButtonOutlinedAutoWidth btnName='Reset' onClick={resetFilters} />
                                <ButtonFilledAutoWidth btnName='Add Subject' onClick={() => navigate('create')} />
                            </div>
                        </div>

                        <div className='flex md:hidden items-center justify-end text-[12px] mt-2'>
                            <h6 className='mr-2 font-[500]'>
                                {page === 1 ? (data?.datas?.length > 0 ? 1 : 0) : (((page - 1) * (data?.pagination?.limit || 0)) + 1)} - {Math.min(page * (data?.pagination?.limit || 0), data?.pagination?.total || 0)} of {data?.pagination?.total || 0}
                            </h6>
                            <IconButton onClick={resetFilters}><BsArrowRepeat size={16} /></IconButton>
                            <div>
                                <IconButton onClick={() => { if (page > 1) setpage(page - 1) }}>
                                    <FiChevronLeft className={`${page === 1 ? 'opacity-50' : 'opacity-100'}`} size={16} />
                                </IconButton>
                                <IconButton onClick={() => { if (page < (data?.pagination?.totalPages || 1)) setpage(page + 1) }}>
                                    <FiChevronRight className={`${data?.pagination?.totalPages === page || !data?.pagination?.totalPages ? 'opacity-50' : 'opacity-100'}`} size={16} />
                                </IconButton>
                            </div>
                        </div>
                    </div>

                    {data?.datas?.length === 0 &&
                        <div className='grid place-items-center mt-20 items-center justify-center'>
                            <img src={'https://fidecrmfiles.s3.amazonaws.com/NoDataFound1.png'} className='w-40 h-40 object-contain' />
                            <h6 className='font-bold text-[14px] -mt-10'>No data found</h6>
                            <h6 className='font-[500] w-[70%] text-center text-slate-700 text-[12.5px] -mt-2'>Oops we couldn't find any data added based on your current page option please click on add the data button to see list here.</h6>
                        </div>
                    }

                    {data?.datas?.length > 0 &&
                        <div className='grid grid-cols-1 md:max-h-[85vh] md:overflow-y-scroll md:grid-cols-5 gap-2 mt-3'>
                            {data?.datas?.map((item) => (
                                <div key={item?._id} className='border items-center relative justify-between px-2 py-2 border-b'>
                                    <h6 className='text-[13px] font-[400]'>Name : <b>{item?.name}</b></h6>
                                    <h6 className='text-[12px] font-[400]'>Standard : <b>{item?.filter_standard?.standard || item?.filter_standard?.label || '-'}</b></h6>
                                    <h6 className='text-[12px] font-[400]'>Stream : <b>{item?.stream || '-'}</b></h6>
                                    <h6 className='text-[12px] font-[400]'>Sub Stream : <b>{item?.substream || '-'}</b></h6>
                                    <h6 className='text-[12px] font-[400]'>Sort Order : <b>{item?.sort_order ?? '-'}</b></h6>
                                    <h6 className='text-[12px] font-[400]'>Chapters : <b>{item?.completed_chapters || 0}/{item?.total_chapters || 0}</b></h6>
                                    <h6 className='text-[10px] font-[400] bg-slate-100 mt-2 p-1'>Created At : <span className='text-[10px] font-[600] leading-normal'>{moment(item?.createdAt).format('LLL')}</span></h6>
                                    <div className='absolute right-1 bg-white top-1.5 flex'>
                                        <Tooltip title='Edit'>
                                            <span><AiOutlineEdit size={14} className='cursor-pointer' onClick={() => { navigate('edit', { state: item }) }} /></span>
                                        </Tooltip>
                                        <Tooltip title='Delete'>
                                            <span><AiOutlineDelete size={14} className='ml-2 cursor-pointer' onClick={() => { setselecteddata(item); setModal(true) }} /></span>
                                        </Tooltip>
                                    </div>
                                </div>
                            ))}
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

export default SubjectList
